import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Bell, LogOut, Menu as MenuIcon, Search, User as UserIcon, Stethoscope } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotificationRefresh } from '../../contexts/NotificationContext'

const TITLES = [
  { pattern: /^\/$/, title: 'Mi Día' },
  { pattern: /^\/citas(\/|$)/, title: 'Mis Citas' },
  { pattern: /^\/mascotas(\/|$)/, title: 'Mis Pacientes' },
  { pattern: /^\/fichas(\/|$)/, title: 'Fichas Clínicas' },
  { pattern: /^\/acceso-mascotas(\/|$)/, title: 'Acceso QR' },
  { pattern: /^\/notificaciones(\/|$)/, title: 'Notificaciones' },
]

function getInitials(user) {
  const source = `${user?.nombre || ''} ${user?.apellido || ''}`.trim()
  if (!source) return 'VB'
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
}

function getTitle(pathname) {
  const entry = TITLES.find((item) => item.pattern.test(pathname))
  return entry?.title || 'Mi Día'
}

function getVetLabel(user) {
  const vet = user?.veterinarios?.[0] || {}
  const name = [user?.nombre, user?.apellido].filter(Boolean).join(' ').trim() || 'Veterinario'
  const specialty =
    vet?.especialidad ||
    vet?.especialidad_nombre ||
    vet?.speciality ||
    'Medicina General'

  return `${name} · ${specialty}`
}

export default function VetBasicTopbar({ onMenuToggle }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { contadorNoLeidas } = useNotificationRefresh()
  const title = getTitle(location.pathname)
  const unreadCount = Number(contadorNoLeidas) > 0 ? Number(contadorNoLeidas) : 0
  const vetLabel = getVetLabel(user)

  return (
    <header className="h-14 border-b border-[#243246] bg-[#151e2d] flex items-center px-4 lg:px-6 gap-3">
      <button
        type="button"
        onClick={onMenuToggle}
        className="lg:hidden h-8 w-8 rounded-lg border border-[#243246] bg-[#1a2535] text-[#7a8fa6] hover:text-[#e8edf5] flex items-center justify-center"
        aria-label="Abrir menú"
      >
        <MenuIcon className="w-4 h-4" />
      </button>

      <h1 className="text-base font-semibold text-[#e8edf5] flex-1 truncate">{title}</h1>

      <div className="hidden xl:flex items-center gap-1.5 bg-[rgba(79,195,247,0.12)] border border-[rgba(79,195,247,0.25)] rounded-lg px-3 py-1 text-xs text-[#4fc3f7]">
        <Stethoscope className="w-3.5 h-3.5" />
        <span className="truncate max-w-[220px]">{vetLabel}</span>
      </div>

      <div className="hidden md:flex items-center gap-2 bg-[#1a2535] border border-[#243246] rounded-[10px] px-3 py-1.5 w-60">
        <Search className="w-3.5 h-3.5 text-[#3d5166] shrink-0" />
        <input
          type="text"
          placeholder="Buscar paciente..."
          className="bg-transparent border-none outline-none text-sm text-[#e8edf5] placeholder:text-[#3d5166] w-full"
          aria-label="Buscar paciente"
        />
      </div>

      <button
        type="button"
        onClick={() => navigate('/notificaciones')}
        className="relative h-8 w-8 rounded-lg border border-[#243246] bg-[#1a2535] text-[#7a8fa6] hover:text-[#e8edf5] flex items-center justify-center"
        aria-label="Notificaciones"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 ? <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#ef5350]" /> : null}
      </button>

      <Menu as="div" className="relative">
        <Menu.Button className="h-8 w-8 rounded-full bg-[#4fc3f7] text-[#0f1623] text-[11px] font-semibold flex items-center justify-center">
          {getInitials(user)}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-120"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-52 origin-top-right rounded-xl border border-[#243246] bg-[#1a2535] shadow-xl focus:outline-none p-1 z-50">
            <div className="px-3 py-2 border-b border-[#243246]">
              <p className="text-xs font-semibold text-[#e8edf5] truncate">
                {[user?.nombre, user?.apellido].filter(Boolean).join(' ') || 'Veterinario'}
              </p>
              <p className="text-[11px] text-[#7a8fa6] truncate">{user?.email || ''}</p>
            </div>

            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => navigate('/perfil')}
                    className={[
                      'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
                      active ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]' : 'text-[#e8edf5]',
                    ].join(' ')}
                  >
                    <UserIcon className="w-4 h-4" />
                    Mi perfil
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={logout}
                    className={[
                      'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
                      active ? 'bg-[rgba(239,83,80,0.12)] text-[#ef5350]' : 'text-[#ef5350]',
                    ].join(' ')}
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  )
}
